import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaShoppingCart, FaStar, FaArrowLeft, FaHeart, FaTruck, FaShieldAlt, FaBox, FaMinus, FaPlus } from 'react-icons/fa';
import ProductReviews from '@/components/ProductReviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Placeholder images by category
const categoryPlaceholders = {
  suplementos: 'https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=600&h=600&fit=crop',
  ropa: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=600&h=600&fit=crop',
  accesorios: 'https://images.unsplash.com/photo-1598289431512-b97b0917affc?w=600&h=600&fit=crop',
  equipos: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=600&fit=crop'
};

const ProductDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/productos`);
      const found = response.data.productos?.find(p => p.id === id);
      if (found) {
        setProduct(found);
      } else {
        toast.error('Producto no encontrado');
        navigate('/tienda');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const getProductImage = (prod) => {
    if (prod?.thumbnail_url) return prod.thumbnail_url;
    return categoryPlaceholders[prod?.category] || categoryPlaceholders.suplementos;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getDiscount = (original, current) => {
    if (!original || original <= current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  const handleAddToCart = async () => {
    setAddingToCart(true);
    // Simular agregar al carrito (aquí conectarías con tu sistema de carrito real)
    setTimeout(() => {
      toast.success(`${quantity} x ${product.name} agregado al carrito`);
      setAddingToCart(false);
    }, 500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const discount = getDiscount(product.original_price, product.price);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="product-detail-page">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button
            onClick={() => navigate('/tienda')}
            variant="ghost"
            className="text-gray-300 hover:text-white hover:bg-zinc-800"
          >
            <FaArrowLeft className="mr-2" /> Volver a la Tienda
          </Button>
        </div>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <div className="aspect-square rounded-2xl overflow-hidden bg-zinc-900 shadow-2xl">
              <img
                src={getProductImage(product)}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = categoryPlaceholders[product.category] || categoryPlaceholders.suplementos;
                }}
              />
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {discount > 0 && (
                  <Badge className="bg-pink-600 text-white px-4 py-2 font-bold text-lg shadow-lg">
                    -{discount}% OFF
                  </Badge>
                )}
              </div>
              {/* Wishlist */}
              <button className="absolute top-4 right-4 w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-pink-600 hover:scale-110 transition-all">
                <FaHeart className="text-xl" />
              </button>
            </div>
          </motion.div>

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Category */}
            <Badge className="bg-pink-600/20 text-pink-400 border border-pink-600/30 px-4 py-1">
              {product.category?.toUpperCase()}
            </Badge>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-black leading-tight">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <FaStar
                    key={i}
                    className={`text-xl ${i < Math.floor(product.rating || 0) ? 'text-amber-400' : 'text-zinc-700'}`}
                  />
                ))}
              </div>
              <span className="text-white font-bold text-lg">{product.rating || 0}</span>
              <span className="text-gray-400">({product.reviews_count || 0} reseñas)</span>
            </div>

            {/* Price */}
            <div className="flex items-end gap-4">
              <span className="text-5xl font-black text-white">
                {formatPrice(product.price)}
              </span>
              {product.original_price > product.price && (
                <span className="text-2xl text-gray-500 line-through mb-1">
                  {formatPrice(product.original_price)}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-300 text-lg leading-relaxed">
              {product.description}
            </p>

            {/* Stock */}
            <div className="flex items-center gap-2">
              <FaBox className="text-green-500" />
              {product.stock > 0 ? (
                <span className="text-green-500 font-medium">
                  {product.stock > 10 ? 'En stock' : `¡Solo quedan ${product.stock} unidades!`}
                </span>
              ) : (
                <span className="text-red-500 font-medium">Agotado</span>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4">
              <span className="text-gray-400">Cantidad:</span>
              <div className="flex items-center bg-zinc-800 rounded-lg">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-3 text-white hover:bg-zinc-700 rounded-l-lg transition-colors"
                >
                  <FaMinus />
                </button>
                <span className="px-6 py-3 font-bold text-lg">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock || 99, quantity + 1))}
                  className="px-4 py-3 text-white hover:bg-zinc-700 rounded-r-lg transition-colors"
                >
                  <FaPlus />
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <Button
              onClick={handleAddToCart}
              disabled={addingToCart || product.stock <= 0}
              className="w-full text-white font-bold py-8 text-xl rounded-full disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
              data-testid="add-to-cart-btn"
            >
              {addingToCart ? (
                'Agregando...'
              ) : product.stock <= 0 ? (
                'Producto Agotado'
              ) : (
                <>
                  <FaShoppingCart className="mr-3" /> Agregar al Carrito - {formatPrice(product.price * quantity)}
                </>
              )}
            </Button>

            {/* Benefits */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              <div className="flex items-center gap-3 p-4 bg-zinc-900 rounded-lg">
                <FaTruck className="text-pink-500 text-xl" />
                <div>
                  <p className="font-medium text-white">Envío Gratis</p>
                  <p className="text-gray-400 text-sm">En compras +$200.000</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-zinc-900 rounded-lg">
                <FaShieldAlt className="text-pink-500 text-xl" />
                <div>
                  <p className="font-medium text-white">Garantía</p>
                  <p className="text-gray-400 text-sm">Calidad asegurada</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Reviews Section */}
        <ProductReviews
          productoId={id}
          productoName={product.name}
          isAuthenticated={!!user}
          currentUser={user}
        />
      </div>
    </div>
  );
};

export default ProductDetail;
